/**
 * useDashboard — GET /v1/clinics/{clinic_id}/dashboard
 *
 * The single biggest query in the app: clinic detail, optional latest
 * meeting, latest scoring snapshot, score cards, trajectory, every active
 * measure with gap analysis and benchmark band, and the rollup summary.
 *
 * `meetingId` is optional. When omitted, the backend picks the most recent
 * meeting for the clinic-year (and `meeting` may be `null` if none exists).
 *
 * Per the handoff §6.2, every mutation invalidates this query.
 */
import { useQuery } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { DashboardResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface UseDashboardOptions {
  /** When `false`, the hook stays idle (returns no data, runs no request). */
  enabled?: boolean;
}

export function useDashboard(
  clinicId: number,
  year: number,
  meetingId?: number,
  options: UseDashboardOptions = {},
) {
  return useQuery({
    queryKey: queryKeys.dashboard.one(clinicId, year, meetingId),
    queryFn: async (): Promise<DashboardResponse> => {
      const { data } = await getApiClient().get<DashboardResponse>(
        `/v1/clinics/${clinicId}/dashboard`,
        {
          params: {
            year,
            ...(meetingId !== undefined ? { meeting_id: meetingId } : {}),
          },
        },
      );
      return data;
    },
    staleTime: 30_000,
    enabled: options.enabled ?? (Number.isFinite(clinicId) && Number.isFinite(year)),
  });
}
